import { motion } from 'framer-motion';
import { CountUp } from '@/components/roi/CountUp';
import { SectionShell } from './SectionShell';
import { BUILD_ALLOCATIONS, MILESTONES } from './use-of-funds-data';

/**
 * Use of funds / capital plan.
 *
 * Surfaces the £3m pre seed target and what it builds, without ever
 * labelling itself "funding ask". Frame the capital as "what we'd build"
 * — runway, team shape and milestones.
 */

export function UseOfFunds() {
  return (
    <SectionShell
      id="capital"
      index={10}
      density="open"
      eyebrow="Pre seed allocation"
      title={
        <>
          A <span className="text-brand-bright">£3m</span> pre seed round
        </>
      }
      intro="A 24 month operating plan: 12 months to build, followed by 12 months of post launch runway for pilot validation and commercial rollout across the engineering, founding and commercial teams"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        {/* Headline envelope card */}
        <motion.article
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative isolate flex flex-col gap-4 overflow-hidden rounded-2xl border border-accent-blue-border bg-gradient-to-br from-bg-card-hover/80 via-bg-card to-accent-blue-bg p-8 shadow-pitch-panel transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-brand-400/70 motion-reduce:transform-none sm:p-10"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -right-24 -z-10 h-56 w-56 rounded-full opacity-[0.16] blur-[100px]"
            style={{ background: '#3B82F6' }}
          />
          <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-brand-bright">
            Pre seed target
          </span>
          <span className="font-semibold tabular-nums text-brand-bright [font-size:clamp(3.25rem,18vw,5rem)] [line-height:0.95]">
            £
            <CountUp
              target={3}
              duration={1.4}
              delay={0.3}
              formatter={(n) => n.toFixed(0)}
            />
            m
          </span>
          <p className="text-[14px] leading-relaxed text-ink-strong">
            Funds the product build, pilot validation and early commercial
            traction
          </p>
          <ul className="mt-3 flex flex-col gap-2 border-t border-accent-blue-border pt-5 text-[13px] leading-relaxed text-ink-strong">
            {MILESTONES.map((m, i) => (
              <motion.li
                key={m}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{
                  duration: 0.4,
                  delay: 0.3 + i * 0.06,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="flex items-start gap-2"
              >
                <span
                  aria-hidden
                  className="mt-[7px] block h-1.5 w-1.5 flex-none rounded-full bg-brand-bright"
                />
                <span>{m}</span>
              </motion.li>
            ))}
          </ul>
        </motion.article>

        {/* Allocation breakdown */}
        <div className="flex flex-col gap-3">
          <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim">
            24 Month Plan · Engineering + Founding + Commercial Team
          </span>
          <div className="flex flex-col gap-3">
            {BUILD_ALLOCATIONS.map((row, i) => (
              <AllocationRow key={row.label} row={row} index={i} />
            ))}
          </div>
          <p className="text-[12px] leading-relaxed text-ink-dim">
            Indicative midpoint allocation across the 24 month plan. Detailed
            cost model held internally; capital is deployed against the
            milestones above
          </p>
        </div>
      </div>
    </SectionShell>
  );
}

function AllocationRow({
  row,
  index,
}: {
  row: (typeof BUILD_ALLOCATIONS)[number];
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.05 + index * 0.06,
      }}
      className="relative isolate flex items-center gap-4 overflow-hidden rounded-2xl border border-accent-blue-border bg-gradient-to-br from-bg-card-hover/80 via-bg-card to-accent-blue-bg p-4 shadow-pitch-panel transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-brand-400/70 motion-reduce:transform-none sm:p-5"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[14px] font-semibold text-ink-strong">
            {row.label}
          </span>
          <span className="shrink-0 whitespace-nowrap text-[13px] font-medium tabular-nums text-brand-bright">
            <CountUp
              target={row.portion}
              duration={1.0}
              delay={0.3 + index * 0.06}
              formatter={(n) => `${Math.round(n)}%`}
            />
          </span>
        </div>
        <p className="mt-1 text-[12.5px] leading-relaxed text-ink-muted">
          {row.detail}
        </p>
        {/* Inline progress bar */}
        <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-border-subtle">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: `${row.portion}%` }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{
              duration: 1.1,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.3 + index * 0.06,
            }}
            className="h-full rounded-full bg-brand-bright"
          />
        </div>
      </div>
    </motion.div>
  );
}
